import { Close as CloseIcon, ConfirmationNumber as TicketIcon } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { API_URL } from '../config/api';

const YandexEventsPanel = ({ token }) => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncingTickets, setSyncingTickets] = useState(false);
    const [error, setError] = useState(null);
    const [syncResult, setSyncResult] = useState(null);
    const [ticketsSyncResult, setTicketsSyncResult] = useState(null);

    // Для модального окна с билетами
    const [ticketsDialogOpen, setTicketsDialogOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(false);

    // Редактирование вместимости
    const [editingCapacity, setEditingCapacity] = useState({}); // { eventId: inputValue }
    const authConfig = {
        headers: {
            Authorization: `Bearer ${token}`
        }
    };

    const handleUpdateCapacity = async (eventId, newValue) => {
        const val = parseInt(newValue);
        if (!val || val < 1) return;
        try {
            await axios.put(`${API_URL}/api/yandex/event/${eventId}/capacity`, { max_tickets: val }, authConfig);
            setEvents(prev => prev.map(e => e.id === eventId ? { ...e, max_tickets: val } : e));
        } catch (err) {
            setError('Ошибка обновления вместимости: ' + (err.response?.data?.error || err.message));
        } finally {
            setEditingCapacity(prev => { const n = { ...prev }; delete n[eventId]; return n; });
        }
    };

    // Форматируем даты для фильтрации
    const getDateString = (daysAgo = 0) => {
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);
        return date.toISOString().split('T')[0];
    };

    const [startDate, setStartDate] = useState(getDateString(7)); // неделя назад
    const [endDate, setEndDate] = useState(getDateString(-30)); // через 30 дней

    // Группировка событий по датам
    const groupEventsByDate = (events) => {
        const grouped = {};
        events.forEach(event => {
            const date = new Date(event.event_date).toLocaleDateString('ru-RU');
            if (!grouped[date]) {
                grouped[date] = [];
            }
            grouped[date].push(event);
        });
        return grouped;
    };

    // Синхронизация событий из Yandex
    const syncEvents = async () => {
        setSyncing(true);
        setError(null);
        setSyncResult(null);

        try {
            const response = await axios.post(`${API_URL}/api/yandex/sync-events`, {}, authConfig);
            setSyncResult(response.data);
            // После синхронизации обновляем список
            fetchEvents();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSyncing(false);
        }
    };

    // Синхронизация билетов из Yandex
    const syncTickets = async () => {
        setSyncingTickets(true);
        setError(null);
        setTicketsSyncResult(null);

        try {
            // Используем широкий диапазон для захвата всех заказов
            // API фильтрует по дате СОЗДАНИЯ заказа, а не дате события
            const currentYear = new Date().getFullYear();
            const response = await axios.post(`${API_URL}/api/yandex/sync-tickets`, {
                date_from: '2024-01-01', // С начала 2024
                date_to: `${currentYear + 1}-12-31` // До конца следующего года
            }, authConfig);
            setTicketsSyncResult(response.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSyncingTickets(false);
        }
    };

    // Полная синхронизация (события + билеты)
    const syncAll = async () => {
        await syncEvents();
        await syncTickets();
    };

    // Получить события из БД
    const fetchEvents = async () => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                date_from: startDate + ' 00:00:00',
                date_to: endDate + ' 23:59:59',
                _t: Date.now() // Cache busting
            };

            const response = await axios.get(`${API_URL}/api/yandex/events`, { ...authConfig, params });
            console.log('📊 Loaded events:', response.data.events?.length);
            if (response.data.events?.length > 0) {
                const firstEvent = response.data.events[0];
                console.log('📋 Sample event fields:', Object.keys(firstEvent));
                console.log('🎫 Has tickets_available?', 'tickets_available' in firstEvent);
            }
            setEvents(response.data.events || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    };

    // Загрузить билеты для события
    const loadTickets = async (eventId) => {
        setLoadingTickets(true);
        try {
            const response = await axios.get(`${API_URL}/api/yandex/event/${eventId}/tickets`, authConfig);
            setTickets(response.data.tickets || []);
            return response.data;
        } catch (err) {
            setError(err.response?.data?.error || err.message);
            return null;
        } finally {
            setLoadingTickets(false);
        }
    };

    // Открыть диалог с билетами
    const handleShowTickets = async (event) => {
        setSelectedEvent(event);
        setTicketsDialogOpen(true);
        await loadTickets(event.id);
    };

    // Закрыть диалог
    const handleCloseTicketsDialog = () => {
        setTicketsDialogOpen(false);
        setSelectedEvent(null);
        setTickets([]);
    };

    useEffect(() => {
        fetchEvents();
    }, [startDate, endDate]);

    // Автоматическая синхронизация при загрузке и каждые 5 минут
    useEffect(() => {
        // Синхронизация при загрузке
        syncAll();

        // Периодическая синхронизация каждые 5 минут
        const interval = setInterval(() => {
            syncAll();
        }, 5 * 60 * 1000); // 5 минут

        return () => clearInterval(interval);
    }, []);

    const groupedEvents = groupEventsByDate(events);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                События Яндекс.Билеты
            </Typography>

            {/* Маркер версии для отладки */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Версия: 2026-01-29 00:22 (с отображением свободных билетов)
            </Typography>

            {/* Контролы */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="С даты"
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextField
                                label="По дату"
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Button
                                variant="contained"
                                onClick={fetchEvents}
                                disabled={loading}
                                fullWidth
                            >
                                Обновить
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                onClick={syncEvents}
                                disabled={syncing}
                                fullWidth
                            >
                                {syncing ? 'Синхр...' : 'События'}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="outlined"
                                onClick={syncTickets}
                                disabled={syncingTickets}
                                fullWidth
                            >
                                {syncingTickets ? 'Синхр...' : 'Билеты'}
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                color="success"
                                onClick={syncAll}
                                disabled={syncing || syncingTickets}
                                fullWidth
                            >
                                Синхр. всё
                            </Button>
                        </Grid>
                    </Grid>

                    {syncResult && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            События: {syncResult.inserted} добавлено, {syncResult.updated} обновлено
                        </Alert>
                    )}
                    {ticketsSyncResult && (
                        <Alert severity="success" sx={{ mt: 2 }}>
                            Билеты: {ticketsSyncResult.synced} синхронизировано, {ticketsSyncResult.errors || 0} ошибок
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Ошибка */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Загрузка */}
            {loading && (
                <Box display="flex" justifyContent="center" p={3}>
                    <CircularProgress />
                </Box>
            )}

            {/* События по датам */}
            {!loading && Object.keys(groupedEvents).length === 0 && (
                <Typography>Нет событий в выбранном диапазоне</Typography>
            )}

            {!loading && Object.keys(groupedEvents).map(date => (
                <Card key={date} sx={{ mb: 2 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            {date}
                        </Typography>
                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Время</TableCell>
                                        <TableCell>Название</TableCell>
                                        <TableCell>Event ID</TableCell>
                                        <TableCell align="center">Билеты</TableCell>
                                        <TableCell>Статус</TableCell>
                                        <TableCell align="right">Действия</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {groupedEvents[date].map(event => {
                                        const ticketsSold = parseInt(event.tickets_sold) || 0;
                                        const ticketsAvailable = parseInt(event.tickets_available) || 0;
                                        const maxTickets = parseInt(event.max_tickets) || 8;
                                        const revenue = parseFloat(event.total_revenue) || 0;

                                        // Отладка
                                        if (event.id === 53258234) {
                                            console.log('Event 53258234 data:', {
                                                tickets_sold: event.tickets_sold,
                                                tickets_available: event.tickets_available,
                                                max_tickets: event.max_tickets,
                                                ticketsSold,
                                                ticketsAvailable,
                                                maxTickets
                                            });
                                        }

                                        return (
                                            <TableRow key={event.id}>
                                                <TableCell>
                                                    {new Date(event.event_date).toLocaleTimeString('ru-RU', {
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </TableCell>
                                                <TableCell>{event.name}</TableCell>
                                                <TableCell>
                                                    <Chip label={event.id} size="small" />
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ minWidth: 120 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: ticketsSold > maxTickets ? 'error.main' : 'inherit' }}>
                                                            🎫 Продано: {ticketsSold}
                                                            {ticketsSold > maxTickets && ' ⚠️'}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.3 }}>
                                                            <Typography variant="caption" color="text.secondary">Макс:</Typography>
                                                            {editingCapacity[event.id] !== undefined ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={editingCapacity[event.id]}
                                                                    onChange={e => setEditingCapacity(prev => ({ ...prev, [event.id]: e.target.value }))}
                                                                    onBlur={() => handleUpdateCapacity(event.id, editingCapacity[event.id])}
                                                                    onKeyDown={e => { if (e.key === 'Enter') handleUpdateCapacity(event.id, editingCapacity[event.id]); if (e.key === 'Escape') setEditingCapacity(prev => { const n = { ...prev }; delete n[event.id]; return n; }); }}
                                                                    autoFocus
                                                                    sx={{ width: 60 }}
                                                                    inputProps={{ min: 1, style: { textAlign: 'center', padding: '2px 4px' } }}
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label={maxTickets}
                                                                    size="small"
                                                                    color={ticketsSold > maxTickets ? 'error' : ticketsAvailable <= 0 ? 'warning' : 'success'}
                                                                    onClick={() => setEditingCapacity(prev => ({ ...prev, [event.id]: maxTickets }))}
                                                                    title="Нажмите чтобы изменить вместимость"
                                                                    sx={{ cursor: 'pointer', fontSize: '0.75rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                        <Typography variant="caption" color={ticketsAvailable < 0 ? 'error.main' : ticketsAvailable === 0 ? 'warning.main' : 'success.main'}>
                                                            {ticketsAvailable < 0 ? `Превышение на ${Math.abs(ticketsAvailable)}` : ticketsAvailable === 0 ? 'Мест нет' : `Свободно: ${ticketsAvailable}`}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            💰 {revenue.toLocaleString('ru-RU')} ₽
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={event.status === 0 ? 'Активно' : 'Другой'}
                                                        color={event.status === 0 ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        startIcon={<TicketIcon />}
                                                        onClick={() => handleShowTickets(event)}
                                                        variant="outlined"
                                                    >
                                                        Билеты
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            ))}

            {/* Диалог с билетами */}
            <Dialog
                open={ticketsDialogOpen}
                onClose={handleCloseTicketsDialog}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">
                            Билеты: {selectedEvent?.name}
                        </Typography>
                        <IconButton onClick={handleCloseTicketsDialog}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                    {selectedEvent && (
                        <Typography variant="body2" color="text.secondary">
                            {new Date(selectedEvent.event_date).toLocaleString('ru-RU')} • Event ID: {selectedEvent.id}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {loadingTickets && (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loadingTickets && tickets.length === 0 && (
                        <Typography>Нет проданных билетов</Typography>
                    )}

                    {!loadingTickets && tickets.length > 0 && (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Всего билетов: {tickets.length} •
                                Активных: {tickets.filter(t => t.status === '1').length}
                            </Alert>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Билет ID</TableCell>
                                            <TableCell>Штрихкод</TableCell>
                                            <TableCell>Посетитель</TableCell>
                                            <TableCell>Телефон</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Тип</TableCell>
                                            <TableCell align="right">Цена</TableCell>
                                            <TableCell>Статус</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {tickets.map(ticket => (
                                            <TableRow key={ticket.id}>
                                                <TableCell>
                                                    <Chip label={ticket.id} size="small" />
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" fontFamily="monospace">
                                                        {ticket.barcode}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>{ticket.visitor_name}</TableCell>
                                                <TableCell>{ticket.visitor_phone}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                        {ticket.visitor_email}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {ticket.rate_name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {ticket.sector}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    ₽{parseFloat(ticket.price).toLocaleString('ru-RU')}
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={ticket.status === '1' ? 'Активен' : 'Другой'}
                                                        color={ticket.status === '1' ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseTicketsDialog}>
                        Закрыть
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YandexEventsPanel;
